import React, { useState } from "react";
import "./OwActionBar.scss";

import * as _ from "lodash-es";
import { Dropdown } from "react-bootstrap";
import IAction from "../../../models/IAction";
import IColumns from "../../../models/IColumns";
import ISupplierModel from "../../../models/ISupplierModel";
import UIService from "../../../services/UIService";
import OwButton from "../OwButton/OwButton";
import OwLabel from "../OwLabel/OwLabel";
import { TableColumnSelect } from "../TableColumnSelect/TableColumnSelect";
import SortOrderEnum from "../../../models/SortOrderEnum";

type Props = {
    actions?: IAction[];
    disabled?: boolean;
    selectedItem?: any;
    isQuotes?: boolean;
    isTemplates?: boolean;
    toggleSmallTable: (isSmallTable: boolean) => void;
    togglePhotoView?: (isPhotoView: boolean) => void;
    columns: IColumns;
    colToggledFn: (columns: IColumns) => void;
    hideTableTypeSelect?: boolean;
    hideColumnToggle?: boolean;
    supplierModel?: ISupplierModel;
    totalValue?: string | number;
    saveAsDefaultsFn?: (columns: IColumns, sortCol?: string, sortOrder?: SortOrderEnum, fixedHeader?: boolean) => void;
    sortCol?: string;
    sortOrder?: SortOrderEnum;
    availSortCol?: { title: string; value: string }[];
    showFixedHeader?: boolean;
    fixedHeader?: boolean;
};

const OwActionBar: React.FC<Props> = ({ actions = [], disabled = true, selectedItem = null, isQuotes = false, ...props }) => {
    const [isSmallTable, setIsSmallTable] = useState(true);
    const [isPhotoView, setIsPhotoView] = useState(false);

    const getTotalActionButtons = (): number => {
        let count = 0;

        for (const action of actions) {
            if (action.isDropdownGroup) {
                count += 1;
            } else if (action.isGroup) {
                if (action.groupActions) {
                    count += action.groupActions.length;
                }
            } else {
                count = count + 1;
            }
        }

        return count;
    };

    const createActionButton = (action: IAction, index: number, totalGroupActions?: number) => {
        let isDisabled = disabled;
        let isActive = false;

        if (action.disabledFn && _.isFunction(action.disabledFn)) {
            isDisabled = action.disabledFn(null);
        }

        if (action.isActive) {
            if (_.isFunction(action.isActive)) {
                isActive = action.isActive(action.name);

                if (isActive) {
                    isDisabled = true;
                }
            } else if (!_.isFunction(action.isActive) && action.isActive) {
                isActive = isDisabled = true;
            }
        }

        let mr = "me-2";

        if (getTotalActionButtons() - 1 === index) {
            mr = "";
        }

        if (totalGroupActions) {
            if (totalGroupActions - 1 === index) {
                mr = "";
            } else if (totalGroupActions === 1) {
                mr = "";
            }
        }

        let name: any = action.name;

        if (action.isSupplierVendorOverride && !_.isNil(props.supplierModel)) {
            if (!_.isNil(props.supplierModel.vendor_name_override)) {
                name = name.replace(/vendor/g, props.supplierModel.vendor_name_override);
            }
        }

        const isOpen = action.isOpen ? action.isOpen() : false;

        let openActiveStateClass = "";

        if (isOpen) {
            openActiveStateClass = "active";
        }

        let btnClass = "";

        if (action.btnClass) {
            if (_.isFunction(action.btnClass)) {
                btnClass = action.btnClass();
            } else {
                btnClass = action.btnClass;
            }
        }

        return (
            <OwButton
                id={action.id}
                key={index}
                className={`btn-sm ${mr} mb-2 mb-md-0 ${openActiveStateClass} ${btnClass ? btnClass : "btn-primary"}`}
                fn={action.fn}
                disabled={isDisabled}
                title={name}
                active={isActive}
                leftIconClass={action.leftIconClass}
            />
        );
    };

    const createDropdownItem = (action: IAction, index: number) => {
        let isDisabled = disabled;

        if (action.disabledFn && _.isFunction(action.disabledFn)) {
            isDisabled = action.disabledFn(null);
        }

        if (action.isActive) {
            if (_.isFunction(action.isActive)) {
                if (action.isActive(action.name)) {
                    isDisabled = true;
                }
            } else if (action.isActive) {
                isDisabled = true;
            }
        }

        let btnClass = "";
        if (action.btnClass) {
            if (_.isFunction(action.btnClass)) {
                btnClass = action.btnClass();
            } else {
                btnClass = action.btnClass;
            }
        }

        // If btnClass contains d-none, don't render the item
        if (btnClass.includes("d-none")) {
            return null;
        }

        let name = action.name;

        if (action.isSupplierVendorOverride && props.supplierModel?.vendor_name_override) {
            name = name.replace(/vendor/g, props.supplierModel.vendor_name_override);
        }

        return (
            <React.Fragment key={index}>
                <Dropdown.Item
                    onClick={(e: React.MouseEvent) => {
                        if (action.fn && !isDisabled) {
                            action.fn(e);
                        }
                    }}
                    disabled={isDisabled}>
                    {action.leftIconClass && <i className={`${action.leftIconClass} me-2`} />}
                    <OwLabel title={name} className="pointer" noBottomMargin={true} />
                </Dropdown.Item>
                {action.hasDividerAfter && <Dropdown.Divider />}
            </React.Fragment>
        );
    };

    const createDropdownGroup = (action: IAction, index: number) => {
        const variant = action.dropdownVariant || "primary";
        const align = action.dropdownAlign || "start";

        // Use fixed strategy to match the CSS override for escaping overflow container
        const popperConfig = {
            strategy: "fixed" as const,
        };

        return (
            <Dropdown key={index} className={`me-2 mb-2 mb-md-0`} data-bs-theme="dark" align={align}>
                <Dropdown.Toggle size="sm" variant={variant} id={action.id}>
                    {action.leftIconClass && <i className={`${action.leftIconClass} me-2`} />}
                    <OwLabel title={action.name} className="pointer" noBottomMargin={true} />
                </Dropdown.Toggle>
                <Dropdown.Menu renderOnMount={true} popperConfig={popperConfig}>
                    {action.groupActions?.map((groupAction, groupIndex) => createDropdownItem(groupAction, groupIndex)).filter(Boolean)}
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    const getActionItems = () => {
        return _.map(actions, (action, index) => {
            // Handle dropdown group
            if (action.isDropdownGroup && action.groupActions) {
                return createDropdownGroup(action, index);
            }

            // Handle fieldset group
            if (action.isGroup && action.groupActions) {
                const groupButtons = action.groupActions.map((groupAction: IAction, groupIndex: number) => {
                    return createActionButton(groupAction, groupIndex, action.groupActions!.length);
                });

                const fieldset = (
                    <fieldset key={index} className={" border p-2 me-1"}>
                        <legend className={"mb-0 w-auto"}>
                            <OwLabel className={"ps-1 pe-1 text-small"} title={action.name} noBottomMargin={true} />
                        </legend>
                        <div>{groupButtons}</div>
                    </fieldset>
                );

                return fieldset;
            } else {
                return createActionButton(action, index);
            }
        });
    };

    const handleToggleSmallTable = (value: boolean) => {
        setIsPhotoView(false);
        setIsSmallTable(value);
        props.toggleSmallTable(value);
    };

    const handleTogglePhotoView = () => {
        setIsPhotoView(true);
        setIsSmallTable(false);
        if (props.togglePhotoView) {
            props.togglePhotoView(true);
        }
    };

    let actionItems: React.ReactNode[] | undefined = getActionItems();

    if (isQuotes) {
        // actionItems = null;
    }

    if (props.isTemplates) {
        actionItems = [];
    }

    let hasGroupActions = false;

    for (const action of actions) {
        if (action.isGroup || action.isDropdownGroup) {
            hasGroupActions = true;
        }
    }

    let cardClass = "dark-card";

    if (UIService.isMainViewLight()) {
        cardClass = "";
    }

    return (
        <div className={"OwActionBar " + cardClass}>
            <div className={"card-body p-2 " + (hasGroupActions ? "align-items-end" : "")}>
                <div className={"action-items d-flex align-items-end"}>{actionItems}</div>
                <div className={"table-items"}>
                    {props.totalValue ? (
                        <div className={"mb-2"}>
                            <OwLabel title={"total_rows"} postText={":"} noBottomMargin={true} className={"me-2"} />
                            {props.totalValue}
                        </div>
                    ) : null}
                    <div className={"d-flex"}>
                        {props.hideTableTypeSelect ? null : (
                            <div>
                                <div className={"btn-group"}>
                                    <OwButton
                                        className={"btn-primary " + (isPhotoView ? "" : isSmallTable ? "active" : "")}
                                        title={""}
                                        centerIconClass={"fad fa-list-alt"}
                                        fn={() => {
                                            handleToggleSmallTable(true);
                                        }}
                                    />
                                    <OwButton
                                        className={"btn-primary " + (isPhotoView ? "" : isSmallTable ? "" : "active")}
                                        title={""}
                                        centerIconClass={"fad fa-list"}
                                        fn={() => {
                                            handleToggleSmallTable(false);
                                        }}
                                    />
                                    {props.togglePhotoView ? (
                                        <OwButton
                                            className={"btn-primary " + (isPhotoView ? "active" : "")}
                                            title={""}
                                            centerIconClass={"fad fa-image"}
                                            fn={() => {
                                                handleTogglePhotoView();
                                            }}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        )}
                        {props.hideColumnToggle ? null : (
                            <div className={"ms-2"}>
                                <TableColumnSelect
                                    className={"table-column-select"}
                                    columns={props.columns}
                                    colToggledFn={props.colToggledFn}
                                    saveAsDefaultFn={props.saveAsDefaultsFn}
                                    sortCol={props.sortCol}
                                    sortOrder={props.sortOrder}
                                    availSortCol={props.availSortCol}
                                    showFixedHeader={props.showFixedHeader}
                                    fixedHeader={props.fixedHeader}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OwActionBar;
